/**
 * Per-subject `lastSeq` store — persisted across reloads.
 *
 * JetStream replay-on-reconnect needs to know the highest sequence number
 * the SDK has already processed for each filter subject. On reconnect (or
 * cold boot after a refresh), the SDK creates JetStream consumers with
 * `opt_start_seq: storedLastSeq + 1` so events missed during the gap are
 * delivered. The in-memory `Map<subject, lastSeq>` that
 * `openWireSubsForAllowSub` used to keep wasn't enough — a page refresh
 * dropped the map and the next mount lost the replay anchor.
 *
 * Storage layout:
 *   `localStorage['morfi-sdk-lastseq']` = JSON object
 *      { [filterSubject]: { seq: number; ts: number } }
 *
 * Writes are throttled (500 ms) so a high-rate trade stream doesn't
 * thrash storage. On RN the same module is intentionally a no-op (no
 * `localStorage`); RN apps can ship an AsyncStorage adapter via a future
 * `setLastSeqAdapter()` if the use case appears.
 */

import type { SyncStorageLike } from '../platform/adapter.js';

const STORAGE_KEY = 'morfi-sdk-lastseq';
const FLUSH_THROTTLE_MS = 500;

interface Entry {
  readonly seq: number;
  /** Wall-clock when the seq was observed — useful for stream-retention
   *  gating (don't trust a `lastSeq` older than the stream's max_age). */
  readonly ts: number;
}

const memory = new Map<string, Entry>();
let loaded = false;
let pendingFlush: ReturnType<typeof setTimeout> | null = null;
let storage: SyncStorageLike | null = null;

/**
 * Inject the storage backend used for `lastSeq` persistence. Called by
 * `<SDKProvider>` once on mount with `adapter.storage.sync`. Without an
 * injected storage the module operates in-memory only — `lastSeq` does
 * not survive a page reload, but the JetStream replay path still works
 * within a single session.
 */
export function setLastSeqStorage(s: SyncStorageLike | null): void {
  storage = s;
}

function loadFromStorage(): void {
  if (loaded) return;
  loaded = true;
  if (storage === null) return;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) return;
    const parsed = JSON.parse(raw) as Record<string, { seq?: unknown; ts?: unknown }>;
    for (const [subject, entry] of Object.entries(parsed)) {
      if (
        typeof entry?.seq === 'number' &&
        Number.isFinite(entry.seq) &&
        typeof entry?.ts === 'number' &&
        Number.isFinite(entry.ts)
      ) {
        memory.set(subject, { seq: entry.seq, ts: entry.ts });
      }
    }
  } catch {
    // Corrupt entry — wipe it; we'll start fresh.
    try {
      storage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

function scheduleFlush(): void {
  if (storage === null) return;
  if (pendingFlush !== null) return;
  pendingFlush = setTimeout(() => {
    pendingFlush = null;
    if (storage === null) return;
    try {
      const snapshot: Record<string, Entry> = {};
      for (const [subject, entry] of memory) snapshot[subject] = entry;
      storage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // Quota exceeded / disabled storage — non-fatal.
    }
  }, FLUSH_THROTTLE_MS);
}

/**
 * Latest sequence seen for `filterSubject`, or `null` if we've never
 * delivered an event matching that filter. The caller passes the FILTER
 * (e.g. `morfi.market.*.trade`), not the concrete subject — that's the
 * key the consumer is bound to.
 */
export function getLastSeq(filterSubject: string): number | null {
  loadFromStorage();
  const e = memory.get(filterSubject);
  return e?.seq ?? null;
}

/**
 * Record that an event with `seq` was delivered under `filterSubject`.
 * Monotonic — older seqs are ignored. Throttled write to localStorage.
 */
export function recordLastSeq(filterSubject: string, seq: number): void {
  loadFromStorage();
  if (!Number.isFinite(seq) || seq <= 0) return;
  const prev = memory.get(filterSubject);
  if (prev !== undefined && prev.seq >= seq) return;
  memory.set(filterSubject, { seq, ts: Date.now() });
  scheduleFlush();
}

/**
 * Drop the stored seq for `filterSubject` — used when the SDK decides the
 * replay backlog is too large and falls back to DeliverNew (skip replay).
 */
export function clearLastSeq(filterSubject: string): void {
  loadFromStorage();
  if (memory.delete(filterSubject)) scheduleFlush();
}

/**
 * Clear all stored seqs. Hooked by the SDK's logout-cache-clear path so
 * cross-account replay anchors don't leak.
 */
export function clearAllLastSeqs(): void {
  loadFromStorage();
  if (memory.size === 0) return;
  memory.clear();
  scheduleFlush();
}

/** Test-only helper to inspect current state. */
export function _peekLastSeqs(): ReadonlyMap<string, Entry> {
  loadFromStorage();
  return memory;
}
